/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

/**
 * Generate a unique ID without uuid dependency
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Timeline data structure extends the existing notes system.
 * Each timeline contains:
 * - id: unique identifier
 * - projectId: associated project ID
 * - name: display name
 * - notes: array of note objects (using existing note schema + x, y, width for timeline)
 * - chapterBorders: array of chapter border objects with x positions
 * - viewState: pan and zoom state
 * - showChapterBorders: toggle for showing/hiding chapter borders
 * - createdAt: creation timestamp
 * - updatedAt: last update timestamp
 */

const initialState = {
  timelines: [],
  activeTimelineId: null,
};

export const timelinesSlice = createSlice({
  name: 'timelines',
  initialState,
  reducers: {
    // Timeline CRUD
    addTimeline: (state, action) => {
      const newTimeline = {
        id: action.payload.id || generateId(),
        projectId: action.payload.projectId || null,
        name: action.payload.name || 'Untitled Timeline',
        notes: action.payload.notes || [],
        chapterBorders: action.payload.chapterBorders || [],
        viewState: action.payload.viewState || {
          panX: 0,
          panY: 0,
          zoom: 1,
        },
        showChapterBorders: action.payload.showChapterBorders ?? true,
        createdAt: action.payload.createdAt || Date.now(),
        updatedAt: Date.now(),
      };
      state.timelines.unshift(newTimeline);
    },

    setTimelines: (state, action) => {
      state.timelines = action.payload;
    },

    updateTimeline: (state, action) => {
      const index = state.timelines.findIndex((tl) => tl.id === action.payload.id);
      if (index !== -1) {
        state.timelines[index] = {
          ...state.timelines[index],
          ...action.payload,
          updatedAt: Date.now(),
        };
      }
    },

    removeTimeline: (state, action) => {
      state.timelines = state.timelines.filter((tl) => tl.id !== action.payload);
      if (state.activeTimelineId === action.payload) {
        state.activeTimelineId = null;
      }
    },

    setActiveTimeline: (state, action) => {
      state.activeTimelineId = action.payload;
    },

    // Note operations within a timeline
    addNoteToTimeline: (state, action) => {
      const { timelineId, note } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        // Extend existing note schema with position/size for timeline
        // Height is fixed for timeline notes (only horizontal resize allowed)
        const timelineNote = {
          ...note,
          _id: note._id || note.id || generateId(),
          x: note.x ?? 100,
          y: note.y ?? 100,
          width: note.width ?? 200,
          height: 200, // Fixed height for timeline notes
        };
        timeline.notes.push(timelineNote);
        timeline.updatedAt = Date.now();
      }
    },

    updateNoteInTimeline: (state, action) => {
      const { timelineId, noteId, updates } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        const noteIndex = timeline.notes.findIndex((n) => (n._id || n.id || n.noteId) === noteId);
        if (noteIndex !== -1) {
          // Prevent height changes (only allow horizontal resizing)
          const safeUpdates = { ...updates };
          if ('height' in safeUpdates) {
            delete safeUpdates.height;
          }
          timeline.notes[noteIndex] = {
            ...timeline.notes[noteIndex],
            ...safeUpdates,
          };
          timeline.updatedAt = Date.now();
        }
      }
    },

    removeNoteFromTimeline: (state, action) => {
      const { timelineId, noteId } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.notes = timeline.notes.filter((n) => (n._id || n.id || n.noteId) !== noteId);
        timeline.updatedAt = Date.now();
      }
    },

    // Chapter border operations
    addChapterBorder: (state, action) => {
      const { timelineId, chapterBorder } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.chapterBorders.push({
          id: chapterBorder.id || generateId(),
          x: chapterBorder.x ?? 500,
          width: chapterBorder.width ?? 400, // Default chapter width
          label: chapterBorder.label || '',
          color: chapterBorder.color || 'gray',
        });
        timeline.updatedAt = Date.now();
      }
    },

    updateChapterBorder: (state, action) => {
      const { timelineId, borderId, updates } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        const borderIndex = timeline.chapterBorders.findIndex((b) => b.id === borderId);
        if (borderIndex !== -1) {
          timeline.chapterBorders[borderIndex] = {
            ...timeline.chapterBorders[borderIndex],
            ...updates,
          };
          timeline.updatedAt = Date.now();
        }
      }
    },

    removeChapterBorder: (state, action) => {
      const { timelineId, borderId } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.chapterBorders = timeline.chapterBorders.filter((b) => b.id !== borderId);
        timeline.updatedAt = Date.now();
      }
    },

    toggleChapterBorders: (state, action) => {
      const { timelineId } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.showChapterBorders = !timeline.showChapterBorders;
        timeline.updatedAt = Date.now();
      }
    },

    setChapterBordersVisibility: (state, action) => {
      const { timelineId, visible } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.showChapterBorders = visible;
        timeline.updatedAt = Date.now();
      }
    },

    // View state operations
    updateViewState: (state, action) => {
      const { timelineId, viewState } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.viewState = {
          ...timeline.viewState,
          ...viewState,
        };
      }
    },

    resetViewState: (state, action) => {
      const { timelineId } = action.payload;
      const timeline = state.timelines.find((tl) => tl.id === timelineId);
      if (timeline) {
        timeline.viewState = {
          panX: 0,
          panY: 0,
          zoom: 1,
        };
      }
    },
  },
});

export const {
  addTimeline,
  setTimelines,
  updateTimeline,
  removeTimeline,
  setActiveTimeline,
  addNoteToTimeline,
  updateNoteInTimeline,
  removeNoteFromTimeline,
  addChapterBorder,
  updateChapterBorder,
  removeChapterBorder,
  toggleChapterBorders,
  setChapterBordersVisibility,
  updateViewState,
  resetViewState,
} = timelinesSlice.actions;

// Selectors
export const selectTimelines = (state) => state.timelines.timelines;
export const selectActiveTimelineId = (state) => state.timelines.activeTimelineId;
export const selectTimelineById = (id) => (state) =>
  state.timelines.timelines.find((tl) => tl.id === id);
export const selectTimelinesByProject = (projectId) => (state) =>
  state.timelines.timelines.filter((tl) => tl.projectId === projectId);

export default timelinesSlice.reducer;
